/**
 * The article's Markdown (what worker/src/block.js bodyHtml renders) ⇄ the editor's document (TipTap JSON). The
 * grammar is ours and small: paragraphs with **bold**, *italic* and [links](url); ## and ### subheads; - and 1. lists;
 * our quote blocks (> "words" / > — who); image figures ![alt](src "caption"); [[clip ...]]; [[vote record]]; and
 * [[chart ...]]. Everything round-trips: docToMarkdown(markdownToDoc(md)) is md, up to whitespace.
 */
export type Mark = { type: "bold" | "italic" | "link"; attrs?: { href: string } };
export type Inline = { type: "text"; text: string; marks?: Mark[] };
export type PNode =
  | { type: "paragraph"; content?: Inline[] }
  | { type: "heading"; attrs: { level: 2 | 3 }; content?: Inline[] }
  | { type: "bulletList" | "orderedList"; content: { type: "listItem"; content: { type: "paragraph"; content?: Inline[] }[] }[] }
  | { type: "ombQuote"; attrs: { who: string }; content: { type: "paragraph"; content?: Inline[] }[] }
  | { type: "ombFigure"; attrs: { src: string; alt: string; caption: string } }
  | { type: "ombClip"; attrs: { meetingId: string; start: string; end: string; caption: string } }
  | { type: "ombVote"; attrs: { record: string } }
  | { type: "ombChart"; attrs: { spec: string } };
export type Doc = { type: "doc"; content: PNode[] };

export const CLIP_LINE = /^\[\[clip\s+([\w-]+)\s+([\d:.]+)(?:-([\d:.]+))?(?:\s+"([^"]*)")?\s*\]\]$/;
export const VOTE_LINE = /^\[\[vote\s+([A-Za-z]+\d{4}-\d+)\s*\]\]$/;
export const CHART_LINE = /^\[\[chart\s+(.+?)\s*\]\]$/;
const IMAGE_LINE = /^!\[([^\]]*)\]\((\S+?)(?:\s+"([^"]*)")?\)$/;

// ---------- inline ----------

/** `**bold**`, `*italic*`/`_italic_` and `[text](url)` → text runs with marks. */
export function parseInline(s: string): Inline[] {
  const out: Inline[] = [];
  const re = /\*\*([^*]+)\*\*|\*([^*\n]+)\*|(?<![\w])_([^_\n]+)_(?![\w])|\[([^\]]+)\]\(([^)\s]+)\)/g;
  let last = 0, m: RegExpExecArray | null;
  const push = (text: string, marks?: Mark[]) => { if (text) out.push(marks?.length ? { type: "text", text, marks } : { type: "text", text }); };
  while ((m = re.exec(s))) {
    push(s.slice(last, m.index));
    if (m[1] != null) push(m[1], [{ type: "bold" }]);
    else if (m[2] != null || m[3] != null) push(m[2] ?? m[3]!, [{ type: "italic" }]);
    else push(m[4]!, [{ type: "link", attrs: { href: m[5]! } }]);
    last = m.index + m[0].length;
  }
  push(s.slice(last));
  return out;
}

export function inlineToMarkdown(content: Inline[] | undefined): string {
  return (content ?? []).map((r) => {
    let t = r.text;
    for (const mk of r.marks ?? []) {
      if (mk.type === "bold") t = `**${t}**`;
      else if (mk.type === "italic") t = `*${t}*`;
      else if (mk.type === "link") t = `[${t}](${mk.attrs?.href ?? ""})`;
    }
    return t;
  }).join("");
}

// ---------- blocks ----------

const para = (text: string): { type: "paragraph"; content?: Inline[] } => { const c = parseInline(text); return c.length ? { type: "paragraph", content: c } : { type: "paragraph" }; };

export function markdownToDoc(md: string): Doc {
  const nodes: PNode[] = [];
  let paraLines: string[] = [], list: { tag: "bulletList" | "orderedList"; items: string[] } | null = null, quote: string[] | null = null;
  const flushPara = () => { if (paraLines.length) { nodes.push(para(paraLines.join(" "))); paraLines = []; } };
  const flushList = () => { if (list) { nodes.push({ type: list.tag, content: list.items.map((i) => ({ type: "listItem", content: [para(i)] })) }); list = null; } };
  const flushQuote = () => {
    if (!quote) return;
    const lines = quote.filter((l) => l.trim());
    const who = lines.length > 1 && /^(—|--)\s*/.test(lines.at(-1)!) ? lines.pop()!.replace(/^(—|--)\s*/, "") : "";
    nodes.push({ type: "ombQuote", attrs: { who }, content: [para(lines.join(" ").replace(/^"|"$/g, ""))] });
    quote = null;
  };
  const flush = () => { flushPara(); flushList(); flushQuote(); };
  for (const raw of String(md || "").replace(/\r/g, "").split("\n")) {
    const line = raw.trimEnd();
    let m: RegExpMatchArray | null;
    if ((m = line.match(CLIP_LINE))) { flush(); nodes.push({ type: "ombClip", attrs: { meetingId: m[1], start: m[2], end: m[3] ?? "", caption: m[4] ?? "" } }); continue; }
    if ((m = line.match(VOTE_LINE))) { flush(); nodes.push({ type: "ombVote", attrs: { record: m[1] } }); continue; }
    if ((m = line.match(CHART_LINE))) { flush(); nodes.push({ type: "ombChart", attrs: { spec: m[1] } }); continue; }
    if ((m = line.match(IMAGE_LINE))) { flush(); nodes.push({ type: "ombFigure", attrs: { alt: m[1], src: m[2], caption: m[3] ?? "" } }); continue; }
    if (/^>\s?/.test(line)) { flushPara(); flushList(); (quote ||= []).push(line.replace(/^>\s?/, "")); continue; }
    flushQuote();
    if ((m = line.match(/^(#{2,3}) (.+)$/))) { flush(); nodes.push({ type: "heading", attrs: { level: m[1].length as 2 | 3 }, content: parseInline(m[2]) }); continue; }
    if ((m = line.match(/^(?:([-*])|(\d+)\.) (.+)$/))) {
      flushPara();
      const tag = m[1] ? "bulletList" : "orderedList";
      if (list && list.tag !== tag) flushList();
      (list ||= { tag, items: [] }).items.push(m[3]);
      continue;
    }
    if (!line.trim()) { flush(); continue; }
    flushList();
    paraLines.push(line.trim());
  }
  flush();
  return { type: "doc", content: nodes.length ? nodes : [{ type: "paragraph" }] };
}

const clock = (t: string) => t;
export function docToMarkdown(doc: Doc | { type: string; content?: unknown[] }): string {
  const out: string[] = [];
  for (const n of (doc.content ?? []) as PNode[]) {
    switch (n.type) {
      case "paragraph": { const t = inlineToMarkdown(n.content); if (t.trim()) out.push(t); break; }
      case "heading": out.push(`${"#".repeat(n.attrs.level)} ${inlineToMarkdown(n.content)}`); break;
      case "bulletList": case "orderedList":
        out.push(n.content.map((li, i) => `${n.type === "bulletList" ? "-" : `${i + 1}.`} ${li.content.map((p) => inlineToMarkdown(p.content)).join(" ")}`).join("\n")); break;
      case "ombQuote": {
        const text = n.content.map((p) => inlineToMarkdown(p.content)).join(" ").trim();
        out.push(`> "${text.replace(/^"|"$/g, "")}"${n.attrs.who ? `\n> — ${n.attrs.who}` : ""}`); break;
      }
      case "ombFigure": out.push(`![${n.attrs.alt}](${n.attrs.src}${n.attrs.caption ? ` "${n.attrs.caption.replace(/"/g, "'")}"` : ""})`); break;
      case "ombClip": out.push(`[[clip ${n.attrs.meetingId} ${clock(n.attrs.start)}${n.attrs.end ? `-${clock(n.attrs.end)}` : ""}${n.attrs.caption ? ` "${n.attrs.caption.replace(/"/g, "'")}"` : ""}]]`); break;
      case "ombVote": out.push(`[[vote ${n.attrs.record}]]`); break;
      case "ombChart": out.push(`[[chart ${n.attrs.spec}]]`); break;
    }
  }
  return out.join("\n\n") + (out.length ? "\n" : "");
}

// ---------- the editor's helpers ----------

/** Pasted text that is our Markdown (blocks, subheads, lists, links or emphasis) rather than plain prose: paste it as blocks. */
export const looksLikeMarkdown = (s: string): boolean =>
  /^(#{2,3} |[-*] |\d+\. |> |\[\[(clip|vote|chart) |!\[)/m.test(s) || /\*\*[^*\n]+\*\*|\[[^\]\n]+\]\([^)\s]+\)/.test(s);

/** A [[chart …]] spec's query back into the picker's fields (the first filter only; the picker edits one). */
export function chartFields(query: string): { groupBy: string; measure: string; filterField: string; filterOp: string; filterValue: string; top: number } {
  const p = new URLSearchParams(query);
  const [filterField = "", filterOp = "is", ...rest] = (p.get("filter") || "").split(":");
  return { groupBy: p.get("groupBy") || "", measure: p.get("measure") || "count", filterField, filterOp: filterOp || "is", filterValue: rest.join(":"), top: Math.max(1, Math.min(50, Number(p.get("top")) || 12)) };
}
